/**
 * SeatGeek Search API Integration
 * Provides search functionality for SeatGeek events with data transformation
 */

import { fetchSeatGeekEvents, transformSeatGeekEventToListing } from './seatgeekApi';
import { UUID } from '../util/types';

/**
 * Search SeatGeek events and transform them to listing format
 * @param {string} keyword - Search keyword
 * @param {Object} options - Search options
 * @param {number} options.size - Number of results to return (default: 10)
 * @param {number} options.page - Page number (default: 1)
 * @returns {Promise<Array>} Array of transformed SeatGeek events as listings
 */
export const searchAndTransformSeatGeekEvents = async (keyword, options = {}) => {
  try {
    const { size = 10, page = 1 } = options;
    const trimmedKeyword = String(keyword || '').trim();

    if (!trimmedKeyword) {
      return [];
    }

    // Search SeatGeek events
    const seatgeekResponse = await fetchSeatGeekEvents({
      q: trimmedKeyword,
      per_page: size,
      page: page,
      sort: 'score.desc', // Get highest scored events first
    });

    // Log first event structure if available (removed debug logging)

    const events = seatgeekResponse.events || [];

    if (events.length === 0) {
      return [];
    }

    // Transform SeatGeek events to listing format
    const transformedEvents = events.map((event, index) => {
      const transformedEvent = transformSeatGeekEventToListing(event);

      return transformedEvent;
    });

    return transformedEvents;
  } catch (error) {
    console.warn('SeatGeek search unavailable; continuing without SeatGeek results.', error.message);
    return [];
  }
};

/**
 * Search SeatGeek performers
 * @param {string} keyword - Search keyword
 * @param {Object} options - Search options
 * @returns {Promise<Array>} Array of SeatGeek performers
 */
export const searchSeatGeekPerformers = async (keyword, options = {}) => {
  try {
    const { size = 10, page = 1 } = options;

    const response = await fetchSeatGeekPerformers({
      q: keyword,
      per_page: size,
      page: page,
    });

    return response.performers || [];
  } catch (error) {
    console.error('🎭 SeatGeek Performers - Error:', error);
    throw error;
  }
};

/**
 * Search SeatGeek venues
 * @param {string} keyword - Search keyword
 * @param {Object} options - Search options
 * @returns {Promise<Array>} Array of SeatGeek venues
 */
export const searchSeatGeekVenues = async (keyword, options = {}) => {
  try {
    const { size = 10, page = 1 } = options;

    const response = await fetchSeatGeekVenues({
      q: keyword,
      per_page: size,
      page: page,
    });

    return response.venues || [];
  } catch (error) {
    console.error('🏟️ SeatGeek Venues - Error:', error);
    throw error;
  }
};

export default {
  searchAndTransformSeatGeekEvents,
  searchSeatGeekPerformers,
  searchSeatGeekVenues,
};
